import { useInvariableEffect } from '@servicetitan/cp-react-hooks';
import { ContentLink, LoadStatus } from '@servicetitan/cp-ui';
import {
    Banner,
    BodyText,
    Button,
    Divider,
    Form,
    Label,
    Link,
    Stack,
} from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { observer } from 'mobx-react';
import { FC, Fragment, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthInviteStore } from '../../stores/invite.store';
import { termsLink } from '../../utils/terms';
import {
    useAuthCodeParams,
    useAuthHistory,
    useAuthInviteQueryParams,
} from '../../utils/use-auth-history';
import { AuthLayout } from '../layout/auth.layout';
import { AuthHeadline } from '../layout/components';
import { passwordError, PasswordInput } from '../ui/password.input';

export const AuthInviteNew: FC<{ code?: string }> = observer(({ code }) => {
    const [{ canSubmit, form, passwordValidator, submit, submitStatus }] =
        useDependencies(AuthInviteStore);
    const { agree, confirm, email, password } = form.$;

    const history = useAuthHistory();
    const isSubmitting = submitStatus === LoadStatus.Loading;

    return (
        <Fragment>
            {submitStatus === LoadStatus.Error && (
                <Banner status="warning" icon title="Registration failed" className="m-b-2">
                    Please contact us for help.
                </Banner>
            )}

            <AuthHeadline
                title="Create Account"
                description="Finish setting up your account by creating a password."
            />

            <Form>
                <Fragment>
                    <Label>Email</Label>
                    <Stack alignItems="center" className="m-b-2 p-b-half">
                        <BodyText className="t-truncate">{email.value}</BodyText>
                    </Stack>
                    <input name="email" type="hidden" value={email.value} />
                </Fragment>

                <PasswordInput
                    data-cy="password"
                    label="Password"
                    value={password.value}
                    onChange={password.onChangeHandler}
                    error={passwordError(passwordValidator, password.error)}
                    disabled={isSubmitting}
                    autoComplete="new-password"
                />

                <PasswordInput
                    data-cy="confirm-password"
                    label="Confirm password"
                    value={confirm.value}
                    onChange={confirm.onChangeHandler}
                    error={confirm.error}
                    disabled={isSubmitting}
                />

                <Form.Checkbox
                    data-cy="terms-agreement"
                    value={agree.value}
                    onChange={agree.onChangeHandler}
                    error={agree.error}
                    disabled={isSubmitting}
                    label={
                        <BodyText>
                            I agree to{' '}
                            <Link href={termsLink} target="_blank" primary>
                                terms of service
                            </Link>
                            .
                        </BodyText>
                    }
                />

                <Button
                    width="full"
                    primary
                    onClick={submit}
                    loading={isSubmitting}
                    disabled={!canSubmit}
                >
                    Create Account
                </Button>

                <BodyText className="m-t-3 ta-center">
                    Have an account?{' '}
                    <ContentLink primary href={history.href('loginOnInvite', { code })}>
                        Sign In
                    </ContentLink>
                </BodyText>
            </Form>
        </Fragment>
    );
});

export const AuthInviteExisted: FC<{ code?: string; createNewUser(): void }> = observer(
    ({ code, createNewUser }) => {
        const [{ authorizedUser, mapStatus, mapUser }] = useDependencies(AuthInviteStore);
        const isMapping = mapStatus === LoadStatus.Loading;

        return (
            <Fragment>
                <AuthHeadline
                    title="Update Account"
                    description="You’ve been invited to access a new workspace."
                />

                <Button width="full" primary onClick={() => mapUser(code)} loading={isMapping}>
                    Continue as {authorizedUser?.name}
                </Button>

                <Divider className="m-y-2">
                    <BodyText size="small" className="p-x-1" subdued>
                        OR
                    </BodyText>
                </Divider>

                <Button width="full" onClick={createNewUser} loading={isMapping}>
                    Create New Account
                </Button>
            </Fragment>
        );
    }
);

const AuthInviteProvider = provide({ singletons: [AuthInviteStore] })(({ children }) => children);

export const AuthInviteComponent = observer(() => {
    const [
        {
            init,
            loadError,
            loadStatus,
            submitStatus,
            /*
             * mapStatus,
             * setShowMergeOption,
             * showMergeOption,
             */
        },
    ] = useDependencies(AuthInviteStore);

    const history = useAuthHistory();
    const rootNavigate = useNavigate();
    const { code } = useAuthCodeParams();
    const { auto } = useAuthInviteQueryParams();

    useInvariableEffect(() => {
        if (!code) {
            history.replace('login');
        } else {
            init(code, auto);
        }
    });

    useEffect(() => {
        if (loadStatus === LoadStatus.Error) {
            if (loadError === 'already_registered') {
                history.replace('login', undefined, { info: 'exists' });
            } else {
                history.replace('register', undefined, { info: 'invite_expired' });
            }
        }
    }, [history, loadError, loadStatus]);

    useEffect(() => {
        if (submitStatus === LoadStatus.Ok /* || mapStatus === LoadStatus.Ok*/) {
            rootNavigate('/', { replace: true });
        }
    }, [rootNavigate, submitStatus]);

    const isLoading = loadStatus !== LoadStatus.Ok;

    return (
        <AuthLayout loading={isLoading}>
            {/* {showMergeOption ? (
                <AuthInviteExisted code={code} createNewUser={() => setShowMergeOption(false)} />
            ) : (
                <AuthInviteNew code={code} />
            )} */}
            <AuthInviteNew code={code} />
        </AuthLayout>
    );
});

export const AuthInvite = () => (
    <AuthInviteProvider>
        <AuthInviteComponent />
    </AuthInviteProvider>
);
